<script setup lang="ts">
/**
 * MY Appointment of Secretary PreSet Panel (M32117)
 *   카테고리: Change Officer (chatflow 는 changeOfficerFlow 와 공유).
 *
 *   3개 문서:
 *     • DCR (이사 서명)            — 1장. 반복부 `[[if secretary]] ... [[/if secretary]]` 로 N명 모두 포함.
 *     • Section 58H (비서 본인)    — 비서 1명당 1장 (N명이면 N장 복제, `new_secretary_1_*` prefix).
 *     • Section 236(3) (비서 본인) — 비서 1명당 1장 (동일하게 N장 복제).
 *
 *   비서 수 무제한 — 배열 `secretaries: SecretaryRow[]` 기반 + Add/Remove 버튼.
 *   Person Lookup — 풀 detail fetch + 인덱스 별 row 채움.
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { useFormatDateByUserFormat, useDateField, parseFlexibleDate } from '@/shared/composables/dateUtils';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  buildSecretaryAppointmentVariables,
  detectSecretaryAppointmentDocFlags,
  type SecretaryEntry,
  type SecretaryQualificationType,
  type GoverningDocumentType,
} from '../mappers/changeOfficerMapper';
import racesData from '../../components/shared/data/common/races.json';
import gendersData from '../../components/shared/data/common/genders.json';
import { OCCUPATIONS, resolveOccupation } from './cdd/cddOptions';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import ContactRegisterModal from '@/features/startTask/preset_task/shared/preset/components/ContactRegisterModal.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';

const raceOptions = racesData as Array<{ value: string; label: string }>;
const genderOptions = gendersData as Array<{ value: string; label: string }>;
// Occupation 은 공용 cddOptions.OCCUPATIONS(케이스 토글 적용) 사용 — CDD/OTC/AOD 와 케이스 일관.
const occupationOptions = OCCUPATIONS;
const qualificationTypeOptions: SecretaryQualificationType[] = ['MAICSA', 'MIA', 'MACS', 'MICPA', 'LS', 'SLS', 'SAA'];

/** occupation 값을 문자열로 정규화 — 객체({value,label})면 label/value, 과거 잘못 저장된 '[object Object]' 면 빈 값.
 *  (contact lookup/prefill 이 객체 또는 오염된 문자열을 넘겨 "[object Object]" 표시되는 것 방지) */
function occStr(v: any): string {
  let s = '';
  if (v == null) s = '';
  else if (typeof v === 'object') {
    s = String((v as any).label ?? (v as any).value ?? (v as any).title ?? (v as any).name ?? '').trim();
  } else {
    s = String(v).trim();
    if (s === '[object Object]') s = '';
  }
  return s ? resolveOccupation(s) : ''; // 공용 케이스 토글(cddOptions) 적용 — CDD/OTC/AOD 와 일관
}

interface PersonLookupContext {
  fieldGroup: 'secretary';
  index: number;
  defaultRole?: 'Secretary' | 'Director' | 'CEO' | 'Contact' | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: PersonLookupContext): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

void props.selectedAddress;
void props.activeAddressContext;

const router = useRouter();

// ── Entity 정보 ──
const icpCd = computed(() => (props.entityData?.icpCd || props.entityData?.entityCd || '') as string);
const entityName = computed(() => (props.entityData?.entityName || props.entityData?.companyName || '') as string);
const regNo = ref('');
const constitutionClauseNumber = ref('');

watch(
  () => (props.entityData?.registrationNo || '') as string,
  (v) => { if (v && !regNo.value) regNo.value = v; },
  { immediate: true }
);

// Entity 자동 정보 채움.
const entityInfoLoading = ref(false);
const articleNoOptions = ref<string[]>([]);
// Entity 등록 article/clause 전체 텍스트 (예: 'Clause 59 of the Company's Constitution') — 카드 괄호 표시용 (AOD 동일).
const articleNo = ref('');
// Entity 에 정관/결의 근거가 등록돼 있으면 true → 라디오 + clause 잠금 (직원 오선택 방지).
const entityClauseRegistered = ref(false);

watch(
  () => icpCd.value,
  async (code) => {
    if (!code) return;
    entityInfoLoading.value = true;
    try {
      const [addrJson, profileJson, directorsJson] = await Promise.all([
        fetch(`/api/setupEntity/changeAddress/address-info/${code}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        // articleNoOptions 전용 경량 엔드포인트 (메모리 캐시) — 무거운 전체 프로필 대신.
        fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        fetch(`/api/documents/variables/directors/${code}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      if (addrJson?.success) {
        if (!regNo.value) regNo.value = addrJson.regNo || '';
        // Governing Document 를 Entity 의 등록 article/clause 텍스트로 자동 판정 + 잠금 (AOD 와 동일).
        //   우선순위: Third Schedule > Constitution > Articles. 등록값 있으면 entityClauseRegistered=true.
        const regClause = String(addrJson.clauseNo || addrJson.articleNo || '').trim();
        articleNo.value = regClause;
        if (regClause) {
          entityClauseRegistered.value = true;
          if (/third\s*schedule/i.test(regClause)) {
            governingDocumentType.value = 'third_schedule';
            constitutionClauseNumber.value = '';
          } else if (/constitution/i.test(regClause)) {
            governingDocumentType.value = 'constitution';
            constitutionClauseNumber.value = parseClauseNo(regClause);
          } else if (/articles?\s+of\s+association/i.test(regClause)) {
            governingDocumentType.value = 'articles';
            constitutionClauseNumber.value = parseClauseNo(regClause);
          } else {
            governingDocumentType.value = 'constitution';
            constitutionClauseNumber.value = parseClauseNo(regClause);
          }
        } else {
          entityClauseRegistered.value = false;
        }
      }
      const opts =
        profileJson?.articleNoOptions ||
        profileJson?.data?.resolution?.articleNoOptions ||
        profileJson?.resolution?.articleNoOptions ||
        [];
      articleNoOptions.value = Array.isArray(opts)
        ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
        : [];
      // 현재 이사 수로 단독/복수 자동 감지
      const directors: any[] =
        directorsJson?.data?.directors ||
        directorsJson?.directors ||
        [];
      directorCount.value = directors.length === 1 ? 'single' : 'multiple';
    } catch (err) {
      console.warn('[PreSetAoS] entity info fetch failed:', err);
    } finally {
      entityInfoLoading.value = false;
    }
  },
  { immediate: true }
);

// DCR governing document type — [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기.
const governingDocumentType = ref<GoverningDocumentType>('constitution');

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  //   setter(clauseOptionType 위임)와 동일 분류 → 탭 튕김 없음.
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 Governing Document 라디오에 맞는 clause 옵션만 노출 (분류 불가 'other' 는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === governingDocumentType.value || ot === 'other';
  })
);
// DCR director count — entity 현재 이사 수로 자동 감지 (기본 multiple).
const directorCount = ref<'single' | 'multiple'>('multiple');

// Constitution Clause parser
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}
const constitutionClauseModel = computed<string>({
  get: () => constitutionClauseNumber.value,
  set: (val: string | null) => {
    const s = String(val || '').trim();
    constitutionClauseNumber.value = parseClauseNo(s);
    // 필터(clauseOptionType)와 동일 분류기로 라디오 자동 감지 — 탭 튕김 방지.
    const detected = clauseOptionType(s);
    if (detected !== 'other') governingDocumentType.value = detected;
  },
});

// ── B15 baseCode 캐시 ──
const b15Options = ref<Array<{ code: string; name: string }>>([]);
const b15CodeMap = ref<Map<string, string>>(new Map());
let b15CacheLoaded = false;
async function ensureB15Cache() {
  if (b15CacheLoaded) return;
  b15CacheLoaded = true;
  try {
    const { baseCodeApi } = await import('@/shared/api/baseCodeApi');
    // L_BASECODE 의 B15 (nationality) 는 COUNTRY_CODE='CM' (common) 으로 저장됨.
    //   `getCodesByMainCodeOnly` 는 country 무관 — CM/MY/SG 등 섞여 반환되어 매핑이 빈약할 수 있음.
    //   `getCodesByMainCode('CM', 'B15')` 로 정확히 CM 마스터만 조회.
    const res: any = await baseCodeApi.getCodesByMainCode('CM', 'B15');
    const items: any[] = res?.data?.codes || res?.codes || res?.data || [];
    const m = new Map<string, string>();
    const list: Array<{ code: string; name: string }> = [];
    for (const it of items) {
      const code = String(it.totalCode || it.TOTAL_CODE || it.subCode || it.SUB_CODE || '').trim();
      const name = String(it.codeName || it.CODE_NAME || '').trim();
      if (code && name) {
        m.set(code, name);
        list.push({ code, name });
      }
    }
    b15CodeMap.value = m;
    b15Options.value = list.sort((a, b) => a.name.localeCompare(b.name));
  } catch { /* ignore */ }
}
void ensureB15Cache();

function b15LabelFromCode(code: string): string {
  if (!code) return '';
  return b15CodeMap.value.get(String(code).toUpperCase()) || code;
}

/** 라벨 (예: 'MALAYSIA') → B15 baseCode (예: 'B15135'). 못 찾으면 빈 문자열. */
function b15CodeFromLabel(label: string): string {
  const target = String(label || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!target) return '';
  for (const [code, name] of b15CodeMap.value) {
    if (String(name).trim().toUpperCase().replace(/\s+/g, ' ') === target) return code;
  }
  return '';
}

/** baseCode 형식이면 캐시 라벨 / 아니면 원본. */
function resolveCountryLabelSync(raw: any): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^B15\d+/i.test(s)) return b15CodeMap.value.get(s.toUpperCase()) || s;
  return s;
}

function toTitleCaseAddress(str: string): string {
  if (!str) return '';
  return str.replace(/[A-Za-z]+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** 주소 후보들 중 city/state/country 포함이 더 완전한 문자열 선택. */
function pickBestAddressLine(...candidates: Array<any>): string {
  const list = candidates
    .map((c) => (c == null ? '' : String(c).trim()))
    .filter(Boolean);
  if (list.length === 0) return '';
  const score = (s: string): number => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const hasMalaysia = /\bmalaysia\b/i.test(s);
    return parts.length * 10 + (hasMalaysia ? 5 : 0) + Math.min(s.length, 200) / 50;
  };
  return list
    .map((s) => ({ s, sc: score(s) }))
    .sort((a, b) => b.sc - a.sc)[0].s;
}

function formatAddrObjToString(addr: any): string {
  if (!addr || typeof addr !== 'object') return '';
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const clean = (v: any): string => {
    const s = String(v == null ? '' : v).trim();
    return UUID_RE.test(s) ? '' : s;
  };
  const country = resolveCountryLabelSync(addr.country || addr.COUNTRY || addr.countryName || '');
  const postCode = clean(addr.postalCode || addr.POSTAL_CODE || addr.post_code || addr.POST_CODE);
  const city = clean(addr.city || addr.CITY);
  const parts = [
    clean(addr.foreignAddressLine1 || addr.FOREIGN_ADDRESS_LINE_1),
    clean(addr.foreignAddressLine2 || addr.FOREIGN_ADDRESS_LINE_2),
    clean(addr.street01 || addr.STREET01),
    clean(addr.street02 || addr.STREET02),
    clean(addr.street03 || addr.STREET03),
    clean(addr.streetName || addr.STREET_NAME),
    clean(addr.buildingName || addr.BUILDING_NAME),
    clean(addr.blockNo || addr.BLOCK_NO),
    [postCode, city].filter(Boolean).join(' '),
    clean(addr.state || addr.STATE),
    country,
  ]
    .map((s) => (s == null ? '' : String(s).trim().replace(/^[\s,.;]+|[\s,.;]+$/g, '').trim()))
    .filter(Boolean);
  return parts.join(', ');
}

// ── 비서 배열 (무제한) ──
interface SecretaryRow {
  fullName: string;
  qualificationType: SecretaryQualificationType | null;
  qualificationNumber: string;
  ssmPractisingCertificateNumber: string;
  contactCd: string;
  // Section 58H / 236(3) 풀 인적정보 (필요할 때만 채움)
  nric: string;
  dob: string; // YYYY-MM-DD
  gender: string;
  race: string;
  nationalityCd: string;
  occupation: string;
  appointmentDate: string; // YYYY-MM-DD
  residentialAddress: string;
  // Section 236(3) 전용
  firm: string;
  tel: string;
  email: string;
  fax: string;
}
function createEmptySecretary(): SecretaryRow {
  return {
    fullName: '', qualificationType: null, qualificationNumber: '',
    ssmPractisingCertificateNumber: '', contactCd: '',
    nric: '', dob: '', gender: '', race: '', nationalityCd: '',
    occupation: '', appointmentDate: '', residentialAddress: '',
    firm: '', tel: '', email: '', fax: '',
  };
}
const secretaries = ref<SecretaryRow[]>([createEmptySecretary()]);

// 행별 date picker 메뉴 (DOB / Appointment Date 분리)
const dobMenu = ref<boolean[]>([false]);
const appointmentMenu = ref<boolean[]>([false]);
// 직접 입력용 텍스트 버퍼 — blur 시에만 파싱 (세션 포맷으로 재표시)
const dobLocalTexts = ref<string[]>(['']);
const appointmentLocalTexts = ref<string[]>(['']);
const formatDate = useFormatDateByUserFormat();
function fmtDate(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return formatDate(d);
  } catch { return iso; }
}
function parseDate(iso: string): Date | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}

// Person Lookup 등 외부에서 row.dob/row.appointmentDate 가 바뀔 때 localText 동기화
watch(
  secretaries,
  (rows) => {
    rows.forEach((row, idx) => {
      dobLocalTexts.value[idx] = fmtDate(row.dob);
      appointmentLocalTexts.value[idx] = fmtDate(row.appointmentDate);
    });
  },
  { deep: true }
);

function _parseToIso(text: string): string {
  const t = text.trim();
  if (!t) return '';
  return (
    parseFlexibleDate(t) ||
    (() => {
      try {
        const d = new Date(t);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      } catch { /* ignore */ }
      return '';
    })()
  );
}
function setDobFromText(idx: number, text: string) {
  const row = secretaries.value[idx];
  if (!row) return;
  if (!text.trim()) { row.dob = ''; return; }
  const parsed = _parseToIso(text);
  if (parsed) { row.dob = parsed; }
  else { dobLocalTexts.value[idx] = fmtDate(row.dob); }
}
function setAppointmentFromText(idx: number, text: string) {
  const row = secretaries.value[idx];
  if (!row) return;
  if (!text.trim()) { row.appointmentDate = ''; return; }
  const parsed = _parseToIso(text);
  if (parsed) { row.appointmentDate = parsed; }
  else { appointmentLocalTexts.value[idx] = fmtDate(row.appointmentDate); }
}

function setRowDate(idx: number, field: 'dob' | 'appointmentDate', val: Date | null, menu: 'dob' | 'appointment') {
  const row = secretaries.value[idx];
  if (!row) return;
  if (!val) {
    row[field] = '';
  } else {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    row[field] = `${y}-${m}-${d}`;
  }
  if (menu === 'dob') dobMenu.value[idx] = false;
  else appointmentMenu.value[idx] = false;
}

// Appointment Date per-row skip (X 버튼으로 blank lodge 허용)
const appointmentSkipped = ref<boolean[]>([false]);

function addSecretary() {
  secretaries.value.push(createEmptySecretary());
  dobMenu.value.push(false);
  appointmentMenu.value.push(false);
  dobLocalTexts.value.push('');
  appointmentLocalTexts.value.push('');
  appointmentSkipped.value.push(false);
}
function removeSecretary(idx: number) {
  if (secretaries.value.length <= 1) return; // 최소 1명 유지
  secretaries.value.splice(idx, 1);
  dobMenu.value.splice(idx, 1);
  appointmentMenu.value.splice(idx, 1);
  dobLocalTexts.value.splice(idx, 1);
  appointmentLocalTexts.value.splice(idx, 1);
  appointmentSkipped.value.splice(idx, 1);
}

// ── 날짜 (회사 결의서) — useDateField: 직접 입력 + 달력 아이콘 트리거 ──
const resolutionDateField = useDateField();
const resolutionDate = resolutionDateField.dateValue;
const resolutionDateMenu = resolutionDateField.dateMenu;
const resolutionDatePickerValue = resolutionDateField.datePickerValue;
const resolutionDatePlaceholder = resolutionDateField.datePlaceholder;
const resolutionDatePickerProps = resolutionDateField.datePickerProps;
const resolutionLocalText = resolutionDateField.localText;
const resolutionOnBlur = resolutionDateField.onDateTextBlur;

const declarationDateField = useDateField();
const dateOfDeclaration = declarationDateField.dateValue;
const declarationDateMenu = declarationDateField.dateMenu;
const declarationDatePickerValue = declarationDateField.datePickerValue;
const declarationDatePlaceholder = declarationDateField.datePlaceholder;
const declarationDatePickerProps = declarationDateField.datePickerProps;
const declarationLocalText = declarationDateField.localText;
const declarationOnBlur = declarationDateField.onDateTextBlur;

// 단일 필드 skip (resolutionDate / dateOfDeclaration)
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(field: string): boolean { return skippedFields.value.has(field); }
function toggleFieldSkip(field: string, skip: boolean) {
  if (skip) skippedFields.value.add(field);
  else skippedFields.value.delete(field);
}
function valOrBlank(field: string, value: string): string {
  return isFieldSkipped(field) ? '' : value;
}

// ── Doc Flags ──
const docFlags = computed(() => detectSecretaryAppointmentDocFlags(props.selection?.documents || []));

// ── Drag & Drop ──
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }
function normalizeIsoDate(v: string): string {
  if (!v) return '';
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }
  return '';
}
function handleFieldDrop(event: DragEvent, setter: (v: string) => void) {
  isDragging.value = false;
  const json = event.dataTransfer?.getData('application/x-entity-field');
  if (json) {
    try { const { value } = JSON.parse(json); if (value) { setter(String(value)); return; } } catch { /* ignore */ }
  }
  const text = event.dataTransfer?.getData('text/plain');
  if (text) setter(String(text));
}
onMounted(() => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

// ── Person Lookup ──
function requestLookup(idx: number) {
  emit('request-person-lookup', { fieldGroup: 'secretary', index: idx, defaultRole: null });
}

watch(
  () => props.selectedPerson,
  async (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx || ctx.fieldGroup !== 'secretary') return;
    const idx = typeof ctx.index === 'number' ? ctx.index : 0;
    // B15 cache 보장 — applyPersonToRow 의 nationality 라벨→코드 lookup 에 필요.
    await ensureB15Cache();
    const cc = String((person as any).contactCd || (person as any).CONTACT_CD || '').trim();
    let detail: any = person;
    if (cc) {
      try {
        const { apiClient } = await import('@/shared/api/client');
        const res: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}`);
        const extra = res?.data?.contact || res?.contact || res?.data || res;
        // merge — 원래 person (addresses[] / primaryAddress / cert 등) 보존하면서
        //   /Contact/:cc 응답이 가진 PROFILE_* / registeredAddress 등으로 보강.
        detail = { ...person, ...extra };
      } catch { /* fallback to person */ }
    }
    applyPersonToRow(detail, idx);
  },
  { deep: true }
);

/**
 * Contact profile JSON_DATA 의 `license_number` 는 type+number 결합 형태로 저장됨.
 *   지원 포맷:
 *     "MAICSA 12345"  → type=MAICSA, number=12345 (공백 구분)
 *     "MIA: 5678"     → type=MIA, number=5678 (콜론 + 공백)
 *     "LS-90"         → type=LS, number=90 (대시)
 *     "LS0009718"     → type=LS, number=0009718 (구분자 없이 붙어있음 — 실제 발견 케이스)
 *     "12345"         → type=null, number=12345 (prefix 없음 → 사용자 직접 선택)
 *   `\b` 사용 시 "LS0" 의 S↔0 경계 (둘 다 word char) 가 boundary 아님 → 매칭 실패. 제거.
 */
function parseLicenseInfo(raw: string): { type: SecretaryQualificationType | null; number: string } {
  const s = String(raw || '').trim();
  if (!s) return { type: null, number: '' };
  const m = s.match(/^(MAICSA|MIA|MICPA|MACS|LS|SLS|SAA)\s*[:\-]?\s*(.*)$/i);
  if (m) {
    const type = m[1].toUpperCase() as SecretaryQualificationType;
    const number = m[2].trim();
    return { type, number: number || '' };
  }
  return { type: null, number: s };
}

// ── 공통 Contact 등록 모달 ──
//   'Register Contact' 클릭 → 모달에서 신규 contact 생성 → created 시 해당 secretary 행에 바인딩.
const contactRegisterOpen = ref(false);
let contactRegisterTargetIndex = -1;
function openContactRegister(idx: number) {
  contactRegisterTargetIndex = idx;
  contactRegisterOpen.value = true;
}
async function onContactRegistered(payload: { contactCd: string; type?: 'individual' | 'corporate'; data: any }) {
  const idx = contactRegisterTargetIndex;
  contactRegisterTargetIndex = -1;
  if (idx < 0 || !payload?.contactCd) return;
  await ensureB15Cache();
  // Person Lookup 과 동일하게 getContactDetail 로 상세(주소 배열 포함) 조회 후 행 바인딩.
  //   applyPersonToRow 가 행을 클리어 후 새 contact 데이터로 채운다(기존 주소/데이터 잔존 없음).
  let detail: any = { contactCd: payload.contactCd };
  try {
    const res: any = await docGenApi.getContactDetail(icpCd.value || '', payload.contactCd);
    const d = res?.data || res;
    if (d) detail = { ...d, contactCd: payload.contactCd, addresses: Array.isArray(d.addresses) ? d.addresses : [] };
  } catch (err) {
    console.warn('[PreSetAppointmentOfSecretary] getContactDetail after register failed:', err);
  }
  applyPersonToRow(detail, idx);
  // detail 로 주소가 안 채워졌으면 모달 입력값으로 보강.
  const row = secretaries.value[idx];
  if (row && !String(row.residentialAddress || '').trim()) {
    const a = buildResidentialFromCreateData(payload.data);
    if (a) row.residentialAddress = a;
  }
}

/** 모달 생성 입력값(CreateIndividualPayload)에서 residential 표시 문자열 조합. GUID/내부코드는 제외. */
function buildResidentialFromCreateData(d: any): string {
  if (!d) return '';
  const s = (x: any) => String(x ?? '').trim();
  const isGuid = (x: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(x);
  const isCode = (x: string) => /^B\d{2}/i.test(x);
  const keepName = (x: string) => !!x && !isGuid(x) && !isCode(x);
  if (d.addressType === 'B07002') {
    const f = d.foreignAddress || {};
    return [f.addressLine1, f.addressLine2, f.addressLine3].map(s).filter(Boolean).join(', ');
  }
  const l = d.localAddress || {};
  const lines = [l.addressLine1, l.addressLine2, l.addressLine3].map(s).filter(Boolean);
  const structured = lines.length ? [] : [
    [s(l.blockHouseNo), s(l.streetName)].filter(Boolean).join(' '),
    s(l.buildingEstateName),
    [s(l.level), s(l.unit)].filter(Boolean).join('-'),
  ].filter(Boolean);
  const postcode = s(l.post_code) || s(l.postalCode) || s(l.postcode);
  const tail = [s(l.city), s(l.state), s(l.country)].filter(keepName);
  return [...lines, ...structured, postcode, ...tail].filter(Boolean).join(', ');
}

function applyPersonToRow(person: any, idx: number) {
  if (!person) return;
  const row = secretaries.value[idx];
  if (!row) return;

  // 다른 사람을 다시 선택하면 이전 사람의 자동 채움 데이터가 남지 않도록 먼저 클리어.
  //   (사용자가 직접 입력하는 appointmentDate / firm / fax 는 보존.)
  //   클리어 후 아래에서 새 사람 데이터로 채움 — "빈 값일 때만 채움" 조건들도 정상 재채움됨.
  row.contactCd = '';
  row.fullName = '';
  row.nric = '';
  row.dob = '';
  row.gender = '';
  row.race = '';
  row.nationalityCd = '';
  row.occupation = '';
  row.residentialAddress = '';
  row.email = '';
  row.tel = '';
  row.qualificationType = null;
  row.qualificationNumber = '';
  row.ssmPractisingCertificateNumber = '';

  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = person[k];
      if (v !== null && v !== undefined && v !== '') return v;
    }
    return '';
  };
  const cc = pick('contactCd', 'contactCode', 'CONTACT_CD');
  if (cc) row.contactCd = String(cc);
  let fullName = pick('fullName', 'contactName', 'name', 'CONTACT_NAME');
  if (!fullName) {
    const first = pick('firstName', 'FIRST_NAME');
    const middle = pick('middleName', 'MIDDLE_NAME');
    const surname = pick('surname', 'SURNAME', 'lastName', 'LAST_NAME');
    fullName = [first, middle, surname].filter(Boolean).join(' ');
  }
  if (fullName) row.fullName = String(fullName);
  const nric = pick('nricNo', 'NRIC_NO', 'passportNo', 'PASSPORT_NO', 'contactRegNo', 'CONTACT_REG_NO');
  if (nric) row.nric = String(nric);
  const dob = pick('dateOfBirth', 'DATE_OF_BIRTH');
  if (dob) row.dob = normalizeIsoDate(dob);
  const gender = String(pick('gender', 'PROFILE_GENDER', 'GENDER')).toLowerCase();
  if (gender) row.gender = gender.startsWith('m') ? 'MALE' : gender.startsWith('f') ? 'FEMALE' : '';
  const raceVal = String(pick('race', 'PROFILE_RACE', 'RACE')).toUpperCase();
  if (raceVal) {
    const m = raceOptions.find((o) => o.value === raceVal || o.label.toUpperCase() === raceVal);
    row.race = m?.value || raceVal;
  }
  // getContactDetail 응답은 nationalityCd(camelCase, B15 코드)로 줌 — camelCase 키도 포함해야 함.
  const natRaw = String(pick('nationality', 'nationalityName', 'nationalityCd', 'NATIONALITY_CD', 'NATIONALITY_NAME')).trim();
  if (natRaw) {
    // B15 baseCode 형태면 그대로, 아니면 라벨로 간주하고 역방향 lookup.
    if (/^B15\d+/i.test(natRaw)) {
      row.nationalityCd = natRaw.toUpperCase();
    } else {
      const code = b15CodeFromLabel(natRaw);
      if (code) row.nationalityCd = code;
    }
  }
  const occ = pick('occupation', 'businessOccupation', 'PROFILE_OCCUPATION', 'BUSINESS_OCCUPATION');
  if (occ) row.occupation = occStr(occ);
  const email = pick('contactEmail', 'CONTACT_EMAIL', 'email');
  if (email) row.email = String(email);
  const mobile = pick('mobileNo', 'MOBILE_NO');
  if (mobile) row.tel = String(mobile);
  // 주소 — AOD 와 동일 후보 조합. MY contact 는 addresses[].type 이 'address' 이므로 residential 만 보면 누락됨.
  const addressList: Array<{ type?: string; label?: string; value?: string }> = Array.isArray((person as any).addresses)
    ? (person as any).addresses
    : [];
  const parseJsonField = (raw: any): any | null => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(String(raw)); } catch { return null; }
  };
  const pickFromList = (...types: string[]): string => {
    for (const t of types) {
      const hit = addressList.find((a) => {
        const type = String(a?.type || '').toLowerCase();
        const label = String(a?.label || '').toLowerCase();
        return (type === t || label.includes(t)) && a?.value;
      });
      const v = hit ? String(hit.value || '').trim() : '';
      if (v) return v;
    }
    const first = addressList.find((a) => String(a?.value || '').trim());
    return first ? String(first.value).trim() : '';
  };
  const normalizeBaseCodeInString = (s: string): string =>
    s.replace(/\bB\d{4,}\b/g, (m) => b15CodeMap.value.get(m.toUpperCase()) || m);
  const jsonObj = parseJsonField(person.RESIDENTIAL_ADDR_JSON_DATA || person.residentialAddrJsonData);
  const fromJson = jsonObj ? formatAddrObjToString(jsonObj) : '';
  const fromList = pickFromList('residential', 'address');
  const resStr = pick('residentialAddress', 'homeAddress', 'primaryAddress', 'PRIMARY_ADDRESS');
  const resObj = person.registeredAddress || person.residential_address || null;
  const resObjRaw = Array.isArray(resObj) ? resObj[0] : resObj;
  const resObjStr = resObjRaw ? formatAddrObjToString(resObjRaw) : '';
  let chosen = pickBestAddressLine(fromList, fromJson, resStr ? String(resStr) : '', resObjStr);
  chosen = toTitleCaseAddress(normalizeBaseCodeInString(chosen));
  if (chosen) row.residentialAddress = chosen;

  // 자격 정보 — Contact profile 의 license_number 자동 채움.
  //   /api/Contact/:cc 응답: PROFILE_SECRETARY_LICENSE_NO (UPPER_CASE).
  //   getContactDetail 응답: licenseNumber (camelCase).
  //   업데이트 직전 호출 (사용자가 이미 입력했으면 덮어쓰지 않음).
  const licenseRaw = pick(
    'PROFILE_SECRETARY_LICENSE_NO',
    'profileSecretaryLicenseNo',
    'licenseNumber',
    'LICENSE_NUMBER',
    'license_number',
  );
  if (licenseRaw) {
    const { type, number } = parseLicenseInfo(String(licenseRaw));
    // 빈 값일 때만 채움 — 사용자 override 보호.
    if (!row.qualificationType && type) row.qualificationType = type;
    if (!row.qualificationNumber.trim() && number) row.qualificationNumber = number;
  }

  // SSM Practising Certificate Number — Contact profile JSON_DATA.SecretaryCertificateNo.
  //   /api/Contact/:cc 와 getContactDetail 응답 양쪽 키 모두 시도.
  const certRaw = pick(
    'ssmPractisingCertificateNumber',
    'SecretaryCertificateNo',
    'secretaryCertificateNo',
    'PROFILE_SSM_PRACTISING_CERTIFICATE_NO',
    'PROFILE_SSM_CERTIFICATE_NO',
    'ssmCertificateNo',
  );
  if (certRaw && !row.ssmPractisingCertificateNumber.trim()) {
    row.ssmPractisingCertificateNumber = String(certRaw).trim();
  }
}

// ── 검증 ──
const showValidationErrors = ref(false);
function hasRowError(idx: number, field: string): boolean {
  if (!showValidationErrors.value) return false;
  const row = secretaries.value[idx];
  if (!row) return false;
  switch (field) {
    case 'fullName': return !row.fullName.trim();
    case 'qualificationType': return !row.qualificationType;
    case 'qualificationNumber': return !row.qualificationNumber.trim();
    case 'nric':
      return (docFlags.value.hasSection58H || docFlags.value.hasSection236_3) && !row.nric.trim();
    case 'appointmentDate':
      return docFlags.value.hasSection58H && !appointmentSkipped.value[idx] && !row.appointmentDate.trim();
  }
  return false;
}
function hasFieldError(field: string): boolean {
  if (!showValidationErrors.value) return false;
  switch (field) {
    case 'constitutionClauseNumber':
      // Third Schedule = clause N/A → 필수 아님.
      return docFlags.value.hasDcr && governingDocumentType.value !== 'third_schedule' && !constitutionClauseNumber.value.trim();
    case 'resolutionDate':
      return docFlags.value.hasDcr && !isFieldSkipped('resolutionDate') && !resolutionDate.value.trim();
    case 'dateOfDeclaration':
      return docFlags.value.hasSection236_3 && !isFieldSkipped('dateOfDeclaration') && !dateOfDeclaration.value.trim();
  }
  return false;
}
const isFormValid = computed<boolean>(() => {
  if (secretaries.value.length === 0) return false;
  for (let i = 0; i < secretaries.value.length; i++) {
    const r = secretaries.value[i];
    if (!r.fullName.trim()) return false;
    if (!r.qualificationType) return false;
    if (!r.qualificationNumber.trim()) return false;
    if ((docFlags.value.hasSection58H || docFlags.value.hasSection236_3) && !r.nric.trim()) return false;
    if (docFlags.value.hasSection58H && !appointmentSkipped.value[i] && !r.appointmentDate.trim()) return false;
  }
  if (docFlags.value.hasDcr) {
    // Third Schedule = clause N/A → 필수 아님.
    if (governingDocumentType.value !== 'third_schedule' && !constitutionClauseNumber.value.trim()) return false;
    if (!isFieldSkipped('resolutionDate') && !resolutionDate.value.trim()) return false;
  }
  if (docFlags.value.hasSection236_3 && !isFieldSkipped('dateOfDeclaration') && !dateOfDeclaration.value.trim()) return false;
  return true;
});

// ── Task config ──
const taskConfigSteps = ref<string[]>([]);
onMounted(async () => {
  const taskCode = props.selection.task.TASK_CODE;
  if (taskCode) {
    try {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = ((cfg as any)?.processSteps as string[]) || [];
    } catch { /* ignore */ }
  }
});

// ── Doc 분류 helpers (per-secretary 복제 대상 식별) ──
function isDcrDoc(name: string): boolean {
  const n = (name || '').toLowerCase();
  return (n.includes('circular resolution') && n.includes('secretar'))
    || (n.includes('directors') && n.includes('resolution') && n.includes('secretar'));
}
function isPerSecretaryDoc(name: string): boolean {
  const n = (name || '').toLowerCase();
  return /section\s*58\s*h/.test(n) || /section\s*236/.test(n);
}

function rowToSecretaryEntry(row: SecretaryRow, skipAppointment = false): SecretaryEntry {
  return {
    fullName: row.fullName,
    qualificationType: row.qualificationType,
    qualificationNumber: row.qualificationNumber,
    ssmPractisingCertificateNumber: row.ssmPractisingCertificateNumber,
    nricOrPassport: row.nric,
    dateOfBirth: row.dob,
    gender: row.gender,
    race: row.race,
    nationalityCd: row.nationalityCd,
    nationality: b15LabelFromCode(row.nationalityCd),
    businessOccupation: occStr(row.occupation),
    dateOfAppointment: skipAppointment ? '' : row.appointmentDate,
    residentialAddress: row.residentialAddress,
    firm: row.firm,
    tel: row.tel,
    email: row.email,
    fax: row.fax,
  };
}

// ── Create Task ──
const creating = ref(false);
const progress = ref('');

// 진행 상태를 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다.
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

async function handleCreateTask() {
  const title = props.taskTitle;
  if (!title) { progress.value = 'Error: Task Title is required.'; return; }
  if (!props.entityData) { progress.value = 'Error: Please select an entity first.'; return; }
  if (!isFormValid.value) {
    showValidationErrors.value = true;
    progress.value = 'Error: Please fill all required secretary fields.';
    return;
  }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task Code not configured.'; return; }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    const secretaryEntries: SecretaryEntry[] = secretaries.value.map((r, i) =>
      rowToSecretaryEntry(r, appointmentSkipped.value[i] ?? false)
    );

    // officerData payload — TASK_DATA.formData 에 저장.
    //   각 secretary row 에 nationality 라벨(B15 baseCode → 코드명) enrich → register detail 표시용.
    const enrichedSecretaries = secretaries.value.map((s, i) => ({
      ...s,
      nationality: b15LabelFromCode(s.nationalityCd),
      appointmentDate: (appointmentSkipped.value[i] ?? false) ? '' : s.appointmentDate,
    }));
    const officerData: Record<string, any> = {
      companyName: entityName.value,
      registrationNo: regNo.value,
      constitutionClauseNumber: constitutionClauseNumber.value,
      resolutionDate: valOrBlank('resolutionDate', resolutionDate.value),
      dateOfDeclaration: valOrBlank('dateOfDeclaration', dateOfDeclaration.value),
      secretaries: enrichedSecretaries,
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    // DCR 용 변수 — 모든 비서 loop 포함 (mapper 의 secretary 배열).
    const dcrVars = buildSecretaryAppointmentVariables({
      companyName: entityName.value,
      registrationNo: regNo.value,
      constitutionClauseNumber: constitutionClauseNumber.value,
      governingDocumentType: governingDocumentType.value,
      directorCount: directorCount.value,
      secretary1: secretaryEntries[0] || null as any,
      secretary2: secretaryEntries[1] || null,
      resolutionDate: valOrBlank('resolutionDate', resolutionDate.value),
      dateOfDeclaration: valOrBlank('dateOfDeclaration', dateOfDeclaration.value),
    });
    // 3명 이상 비서 — mapper 가 _1/_2 만 지원하지만 새 반복부 (`secretary` loop) 는 모두 포함.
    //   loop 배열을 풀 명단으로 재할당.
    dcrVars.secretary = secretaryEntries
      .filter((s) => String(s.fullName || '').trim())
      .map((s) => ({
        new_secretary_full_name: s.fullName.trim(),
        new_secretary_license_type: String(s.qualificationType || '').trim(),
        new_secretary_license_number: String(s.qualificationNumber || '').trim(),
        new_secretary_ssm_practising_certificate_number: String(s.ssmPractisingCertificateNumber || '').trim(),
      }));
    // 새 DCR 템플릿: 복수형 loop key([[if secretaries]]) + 비서 단/복수 플래그를 실제 N명 기준으로 보정.
    dcrVars.secretaries = dcrVars.secretary;
    const _secCount = dcrVars.secretary.length;
    dcrVars.appoint_single_secretary = _secCount <= 1;
    dcrVars.appoint_multiple_secretaries = _secCount > 1;

    // 문서 빌드 — DCR 1장 + (Section 58H, 236(3)) 는 비서당 1장 복제.
    const docsToSave: Array<Record<string, any>> = [];
    for (const d of props.selection.documents || []) {
      if (!d.tmpId) continue;
      const name = d.templateName || '';
      if (isPerSecretaryDoc(name)) {
        // Section 58H 는 기존대로 자동 셋팅 안 함 — detail 에서 고객이 사이너 직접 지정 (manual).
        //   그 외 per-secretary 문서(236(3) 등)는 비서 본인을 서명자로 자동 바인딩 → SIGNER_JSON 저장.
        const is58H = /section\s*58\s*h/i.test(name);
        // 각 비서마다 한 장씩 — formData 의 `new_secretary_1_*` prefix 변수에 해당 비서 데이터 주입.
        secretaryEntries.forEach((sec, i) => {
          const row = secretaries.value[i];
          const secSigners = sec.fullName?.trim()
            ? [{ id: row?.contactCd || undefined, name: sec.fullName.trim(), title: 'Secretary', showTitle: false, _signerLayout: 'box', role: 'signer' }]
            : undefined;
          // 58H 는 빈 배열(미치환/수동) 유지, 그 외는 비서 자동 바인딩.
          const docSigners = is58H ? undefined : secSigners;
          const perSecVars = buildSecretaryAppointmentVariables({
            companyName: entityName.value,
            registrationNo: regNo.value,
            constitutionClauseNumber: constitutionClauseNumber.value,
            governingDocumentType: governingDocumentType.value,
            directorCount: directorCount.value,
            secretary1: sec,
            secretary2: null,
            resolutionDate: valOrBlank('resolutionDate', resolutionDate.value),
            dateOfDeclaration: valOrBlank('dateOfDeclaration', dateOfDeclaration.value),
            // 58H 는 빈 배열 → [[Signers]] 미치환 → detail 에서 수동 지정.
            signers: is58H ? [] : (secSigners || []),
          });
          docsToSave.push({
            templateId: d.tmpId,
            label: `${d.templateName} — ${sec.fullName || `#${i + 1}`}`,
            personName: sec.fullName || entityName.value || '',
            formData: { ...perSecVars },
            entityCd: icpCd.value || undefined,
            // top-level signers → 백엔드가 SIGNER_JSON 으로 저장 (58H 는 undefined → 미저장, 기존과 동일).
            signers: docSigners,
          });
        });
      } else if (isDcrDoc(name)) {
        docsToSave.push({
          templateId: d.tmpId,
          label: d.templateName,
          personName: entityName.value || '',
          formData: { ...dcrVars },
          entityCd: icpCd.value || undefined,
        });
      } else {
        // 알 수 없는 문서는 DCR 변수 세트로 보냄 (안전 default).
        docsToSave.push({
          templateId: d.tmpId,
          label: d.templateName,
          personName: entityName.value || '',
          formData: { ...dcrVars },
          entityCd: icpCd.value || undefined,
        });
      }
    }

    if (docsToSave.length > 0) {
      try {
        await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
      } catch (err: any) {
        console.warn('[PreSetAoS] Document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    progress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}

// 문서 미리보기 list (예상 생성 매수)
const docsPreviewList = computed<Array<{ name: string; count: number; type: string }>>(() => {
  const out: Array<{ name: string; count: number; type: string }> = [];
  const validSecCount = secretaries.value.filter((s) => s.fullName.trim()).length || secretaries.value.length;
  for (const d of props.selection.documents || []) {
    const name = d.templateName || '';
    if (isPerSecretaryDoc(name)) {
      out.push({ name, count: validSecCount, type: 'per-secretary' });
    } else {
      out.push({ name, count: 1, type: 'single' });
    }
  }
  return out;
});

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
function gatherState(): Record<string, any> {
  return {
    regNo: regNo.value,
    constitutionClauseNumber: constitutionClauseNumber.value,
    governingDocumentType: governingDocumentType.value,
    directorCount: directorCount.value,
    secretaries: JSON.parse(JSON.stringify(secretaries.value || [])),
    appointmentSkipped: [...appointmentSkipped.value],
    resolutionDate: resolutionDate.value,
    dateOfDeclaration: dateOfDeclaration.value,
    skippedFields: Array.from(skippedFields.value),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.regNo != null) regNo.value = s.regNo;
  // Entity 등록값이 있으면(entityClauseRegistered) entity 가 authoritative — 저장된 값으로 덮어쓰지 않음.
  if (!entityClauseRegistered.value) {
    if (s.constitutionClauseNumber != null) constitutionClauseNumber.value = s.constitutionClauseNumber;
    if (s.governingDocumentType != null) governingDocumentType.value = s.governingDocumentType;
  }
  if (s.directorCount === 'single' || s.directorCount === 'multiple') directorCount.value = s.directorCount;
  if (Array.isArray(s.secretaries) && s.secretaries.length > 0) {
    secretaries.value = s.secretaries.map((row: any) => ({ ...createEmptySecretary(), ...row }));
    // 행별 보조 배열 길이 동기화 (date menu / local text buffers).
    const n = secretaries.value.length;
    dobMenu.value = Array.from({ length: n }, () => false);
    appointmentMenu.value = Array.from({ length: n }, () => false);
    dobLocalTexts.value = secretaries.value.map((r) => fmtDate(r.dob));
    appointmentLocalTexts.value = secretaries.value.map((r) => fmtDate(r.appointmentDate));
  }
  if (Array.isArray(s.appointmentSkipped)) {
    const n = secretaries.value.length;
    appointmentSkipped.value = Array.from({ length: n }, (_, i) => !!s.appointmentSkipped[i]);
  }
  if (s.resolutionDate != null) resolutionDate.value = s.resolutionDate;
  if (s.dateOfDeclaration != null) dateOfDeclaration.value = s.dateOfDeclaration;
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  // watcher(localText 동기화 등)가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="aos-panel">
    <!-- Header -->
    <div class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Appointment of Secretary</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <!-- Task Title -->
    <div class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:tag-outline" width="14" color="#888" />
        <span>Task Title</span>
      </div>
      <div class="task-title-box">
        <span>{{ taskTitle || '—' }}</span>
        <v-btn size="x-small" variant="tonal" color="primary" @click="emit('change-task-title')">
          Change
        </v-btn>
      </div>
    </div>

    <!-- Entity -->
    <div class="co-section">
      <div v-if="icpCd" class="entity-card">
        <Icon icon="mdi:domain" width="22" color="#3b82f6" />
        <div class="entity-card-info">
          <div class="entity-card-name">{{ entityName || icpCd }}</div>
          <div class="entity-card-meta">
            <span>Registration No: <strong>{{ regNo || '—' }}</strong></span>
            <span v-if="constitutionClauseNumber" class="ml-2">
              · Clause: <strong>{{ constitutionClauseNumber }}</strong>
              <span v-if="articleNo" class="text-grey-darken-1"> ({{ articleNo }})</span>
            </span>
            <span v-if="entityInfoLoading" class="ml-2 text-grey">loading...</span>
          </div>
        </div>
        <v-btn size="x-small" variant="tonal" color="primary" @click="emit('change-entity')">
          Change
        </v-btn>
      </div>
      <div v-else class="co-empty">
        <Icon icon="mdi:domain" width="18" color="#888" />
        <span>Select an entity in the chat to begin.</span>
      </div>
    </div>

    <!-- Company (Constitution Clause) - DCR 일 때 -->
    <div v-if="icpCd && docFlags.hasDcr" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:domain-plus" width="14" color="#888" />
        <span>Company</span>
        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">DCR</v-chip>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <!-- Third Schedule: 조항 N/A -->
          <v-text-field
            v-if="governingDocumentType === 'third_schedule'"
            model-value="N/A"
            disabled
            :label="entityClauseRegistered ? 'Constitution Clause No (registered)' : 'Constitution Clause No'"
            variant="outlined" density="compact" hide-details="auto"
          />
          <!-- Entity 등록 정관/article → readonly 잠금 -->
          <v-text-field
            v-else-if="entityClauseRegistered"
            v-model="constitutionClauseNumber"
            :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ' (registered)'"
            readonly append-inner-icon="mdi-lock"
            variant="outlined" density="compact" hide-details="auto"
            persistent-hint hint="Registered in entity profile (locked)"
          />
          <!-- 미등록 → 선택/입력 -->
          <v-combobox
            v-else
            v-model="constitutionClauseModel"
            :items="filteredClauseOptions"
            :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ' *'"
            :placeholder="governingDocumentType === 'articles' ? 'Select or type article number' : 'Select or type clause number'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            clearable
            :error="hasFieldError('constitutionClauseNumber')"
            :error-messages="hasFieldError('constitutionClauseNumber') ? ['Required'] : []"
            :hint="
              filteredClauseOptions.length > 0
                ? `${filteredClauseOptions.length} option(s) — only the number is stored`
                : 'No options found — type manually'
            "
            persistent-hint
          />
        </v-col>
        <v-col cols="12" class="mt-1">
          <div class="co-field-label">
            Governing Document
            <v-icon v-if="entityClauseRegistered" size="12" class="ml-1">mdi-lock</v-icon>
            <span v-if="entityClauseRegistered" class="text-caption text-grey-darken-1 ml-1">(registered in entity — locked)</span>
          </div>
          <v-radio-group v-model="governingDocumentType" @update:model-value="constitutionClauseNo = ''" inline density="compact" hide-details :disabled="entityClauseRegistered">
            <v-radio label="Constitution" value="constitution" />
            <v-radio label="Articles of Association" value="articles" />
            <v-radio label="Third Schedule (Companies Act 2016)" value="third_schedule" />
          </v-radio-group>
        </v-col>
        <v-col cols="12">
          <div class="text-caption text-grey-darken-1">
            Director count: <strong>{{ directorCount === 'single' ? 'Single Director' : 'Multiple Directors' }}</strong>
            <span class="text-grey"> (auto-detected)</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- 비서 배열 — Add/Remove -->
    <div v-if="icpCd && docFlags.needsNewSecretaryForm" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:account-multiple" width="14" color="#888" />
        <span>Secretaries</span>
        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
          {{ secretaries.length }} appointee(s)
        </v-chip>
        <v-chip
          v-if="docFlags.hasSection58H || docFlags.hasSection236_3"
          size="x-small"
          variant="flat"
          color="info"
          class="ml-1"
        >
          1 sheet per secretary (58H / 236(3))
        </v-chip>
        <v-spacer />
        <v-btn size="x-small" variant="tonal" color="primary" @click="addSecretary">
          <v-icon start size="14">mdi-plus</v-icon>
          Add Secretary
        </v-btn>
      </div>

      <div v-for="(row, idx) in secretaries" :key="idx" class="sec-row">
        <div class="sec-row-header">
          <Icon icon="mdi:account-tie" width="16" color="#3b82f6" />
          <strong>Secretary {{ idx + 1 }}</strong>
          <v-spacer />
          <v-btn size="x-small" variant="tonal" color="primary" class="me-2" @click="openContactRegister(idx)">
            <v-icon start size="14">mdi-account-plus-outline</v-icon>
            Register Contact
          </v-btn>
          <v-btn
            v-if="secretaries.length > 1"
            size="x-small"
            variant="tonal"
            color="error"
            @click="removeSecretary(idx)"
          >
            <v-icon start size="14">mdi-minus</v-icon>
            Remove
          </v-btn>
        </div>

        <v-row dense>
          <v-col cols="12" md="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="row.fullName"
                label="Full Name *"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :error="hasRowError(idx, 'fullName')"
                :error-messages="hasRowError(idx, 'fullName') ? ['Required'] : []"
              >
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="requestLookup(idx)"
                      />
                    </template>
                    <span>Lookup person from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div
                v-if="isDragging"
                class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => row.fullName = v)"
              />
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="row.qualificationType"
              :items="qualificationTypeOptions"
              label="Qualification Type *"
              variant="outlined"
              density="compact"
              hide-details="auto"
              clearable
              :error="hasRowError(idx, 'qualificationType')"
              :error-messages="hasRowError(idx, 'qualificationType') ? ['Required'] : []"
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="row.qualificationNumber"
                :label="`${row.qualificationType || 'License'} Number *`"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :error="hasRowError(idx, 'qualificationNumber')"
                :error-messages="hasRowError(idx, 'qualificationNumber') ? ['Required'] : []"
              />
              <div
                v-if="isDragging"
                class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => row.qualificationNumber = v)"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="row.ssmPractisingCertificateNumber"
                label="SSM Practising Certificate Number"
                variant="outlined"
                density="compact"
                hide-details
              />
              <div
                v-if="isDragging"
                class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => row.ssmPractisingCertificateNumber = v)"
              />
            </div>
          </v-col>

          <!-- Section 58H / 236(3) 일 때 노출되는 인적정보 -->
          <template v-if="docFlags.hasSection58H || docFlags.hasSection236_3">
            <v-col cols="12" md="6">
              <div class="co-drop-wrap">
                <v-text-field
                  v-model="row.nric"
                  label="NRIC / Passport No *"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :error="hasRowError(idx, 'nric')"
                  :error-messages="hasRowError(idx, 'nric') ? ['Required'] : []"
                />
                <div
                  v-if="isDragging"
                  class="co-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => row.nric = v)"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="dobLocalTexts[idx]"
                label="Date of Birth"
                variant="outlined"
                density="compact"
                hide-details
                :placeholder="resolutionDatePlaceholder"
                @blur="setDobFromText(idx, $event.target.value)"
              >
                <template #append-inner>
                  <v-menu v-model="dobMenu[idx]" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      :model-value="parseDate(row.dob)"
                      @update:model-value="(v: any) => setRowDate(idx, 'dob', v as Date | null, 'dob')"
                    />
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="row.gender"
                :items="genderOptions"
                item-title="label"
                item-value="value"
                label="Gender"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="row.race"
                :items="raceOptions"
                item-title="label"
                item-value="value"
                label="Race"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="row.nationalityCd"
                :items="b15Options"
                item-title="name"
                item-value="code"
                label="Nationality"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="row.occupation"
                :items="occupationOptions"
                item-title="label"
                item-value="value"
                label="Business / Occupation"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </v-col>
            <v-col v-if="docFlags.hasSection58H" cols="12" md="6">
              <v-text-field
                v-model="appointmentLocalTexts[idx]"
                :label="appointmentSkipped[idx] ? 'Date of Appointment' : 'Date of Appointment *'"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :placeholder="resolutionDatePlaceholder"
                :class="{ 'co-field-skipped': appointmentSkipped[idx] }"
                :error="hasRowError(idx, 'appointmentDate')"
                :error-messages="hasRowError(idx, 'appointmentDate') ? ['Required'] : []"
                @blur="setAppointmentFromText(idx, $event.target.value)"
              >
                <template v-if="!appointmentSkipped[idx]" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn"
                        @mousedown.prevent
                        @click.stop="appointmentSkipped[idx] = true">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip this field (lodge as blank)
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="appointmentSkipped[idx]" class="co-restore-link"
                    @click.stop="appointmentSkipped[idx] = false">Restore</a>
                  <v-menu v-else v-model="appointmentMenu[idx]" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      :model-value="parseDate(row.appointmentDate)"
                      @update:model-value="(v: any) => setRowDate(idx, 'appointmentDate', v as Date | null, 'appointment')"
                    />
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col v-if="docFlags.hasSection58H" cols="12">
              <div class="co-drop-wrap">
                <v-textarea
                  v-model="row.residentialAddress"
                  label="Residential Address"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  auto-grow
                  hide-details
                />
                <div
                  v-if="isDragging"
                  class="co-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => row.residentialAddress = v)"
                />
              </div>
            </v-col>
          </template>

          <!-- Section 236(3) 추가 필드 -->
          <template v-if="docFlags.hasSection236_3">
            <v-col cols="12" md="6">
              <div class="co-drop-wrap">
                <v-text-field v-model="row.firm" label="Firm" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => row.firm = v)" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="co-drop-wrap">
                <v-text-field v-model="row.tel" label="Tel" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => row.tel = v)" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="co-drop-wrap">
                <v-text-field v-model="row.email" label="Email" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => row.email = v)" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="co-drop-wrap">
                <v-text-field v-model="row.fax" label="Fax" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => row.fax = v)" />
              </div>
            </v-col>
          </template>
        </v-row>
      </div>
    </div>

    <!-- 날짜 -->
    <div v-if="icpCd && (docFlags.hasDcr || docFlags.hasSection236_3)" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:calendar-multiple" width="14" color="#888" />
        <span>Dates</span>
      </div>
      <v-row dense>
        <v-col v-if="docFlags.hasDcr" cols="12" md="6">
          <v-text-field
            v-model="resolutionLocalText"
            :placeholder="resolutionDatePlaceholder"
            :label="isFieldSkipped('resolutionDate') ? 'Resolution Date' : 'Resolution Date *'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :class="{ 'co-field-skipped': isFieldSkipped('resolutionDate') }"
            :error="hasFieldError('resolutionDate')"
            :error-messages="hasFieldError('resolutionDate') ? ['Required'] : []"
            @blur="resolutionOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="co-skip-btn"
                    @mousedown.prevent
                    @click.stop="toggleFieldSkip('resolutionDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('resolutionDate')" class="co-restore-link"
                @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
              <v-menu v-else v-model="resolutionDateMenu" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="resolutionDatePickerProps"
                  v-model="resolutionDatePickerValue"
                  @update:model-value="resolutionDateMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
          <div class="text-caption text-grey-darken-1 mt-1">For DCR</div>
        </v-col>
        <v-col v-if="docFlags.hasSection236_3" cols="12" md="6">
          <v-text-field
            v-model="declarationLocalText"
            :placeholder="declarationDatePlaceholder"
            :label="isFieldSkipped('dateOfDeclaration') ? 'Date of Declaration' : 'Date of Declaration *'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :class="{ 'co-field-skipped': isFieldSkipped('dateOfDeclaration') }"
            :error="hasFieldError('dateOfDeclaration')"
            :error-messages="hasFieldError('dateOfDeclaration') ? ['Required'] : []"
            @blur="declarationOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('dateOfDeclaration')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="co-skip-btn"
                    @mousedown.prevent
                    @click.stop="toggleFieldSkip('dateOfDeclaration', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('dateOfDeclaration')" class="co-restore-link"
                @click.stop="toggleFieldSkip('dateOfDeclaration', false)">Restore</a>
              <v-menu v-else v-model="declarationDateMenu" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="declarationDatePickerProps"
                  v-model="declarationDatePickerValue"
                  @update:model-value="declarationDateMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
          <div class="text-caption text-grey-darken-1 mt-1">For Section 236(3)</div>
        </v-col>
      </v-row>
    </div>

    <!-- Documents preview — 비서수 × Section 58H/236(3) 복제 매수 미리 표시 -->
    <div v-if="icpCd && docsPreviewList.length > 0" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:file-multiple-outline" width="14" color="#888" />
        <span>Documents to be generated</span>
      </div>
      <div class="doc-list">
        <div v-for="(d, i) in docsPreviewList" :key="i" class="doc-list-item">
          <v-icon size="14" color="success">mdi-check-circle</v-icon>
          <span>{{ d.name }}</span>
          <v-chip size="x-small" variant="tonal" :color="d.type === 'per-secretary' ? 'warning' : 'primary'" class="ml-2">
            × {{ d.count }}
          </v-chip>
        </div>
      </div>
    </div>

    <div v-if="progress" class="progress-line text-caption" :class="{ 'text-error': progress.startsWith('Error:') }">
      {{ progress }}
    </div>

    <div class="co-footer">
      <v-btn variant="outlined" @click="emit('back')">Back</v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              color="primary"
              variant="flat"
              :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
              :loading="creating"
              :disabled="creating || (editMode && !isDirty)"
              @click="handleCreateTask"
            >
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- 공통 Contact 등록 모달 — 신규 contact 생성 후 secretary 행 자동 채움. -->
    <ContactRegisterModal v-model="contactRegisterOpen" @created="onContactRegistered" />
  </div>
</template>

<style scoped>
.aos-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 16px;
  gap: 12px;
  background: #fafbfd;
  overflow-y: auto;
}
.co-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}
.co-title { font-size: 17px; font-weight: 700; color: #1f2937; }
.co-subtitle { font-size: 12px; color: #6b7280; }

.co-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}
.co-section-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: #374151;
  margin-bottom: 8px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-radius: 6px;
  padding: 6px 8px;
}
.task-title-box {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px;
  background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 6px;
  font-size: 13px; color: #334155;
}
.entity-card {
  display: flex; align-items: center; gap: 12px;
  padding: 12px;
  background: #f0f5ff; border: 1px solid #c7d6ff; border-radius: 8px;
}
.entity-card-info { flex: 1; }
.entity-card-name { font-size: 14px; font-weight: 700; color: #1e3a8a; }
.entity-card-meta { font-size: 12px; color: #475569; }

.co-empty {
  display: flex; align-items: center; gap: 8px;
  padding: 16px;
  background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 6px;
  font-size: 12px; color: #64748b; justify-content: center;
}

.sec-row {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}
.sec-row:last-child { margin-bottom: 0; }
.sec-row-header {
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 8px;
  font-size: 13px; color: #1f2937;
}

.co-drop-wrap { position: relative; }
.co-drop-overlay {
  position: absolute; inset: 0;
  border: 2px dashed #6366f1; background: rgba(99, 102, 241, 0.05);
  border-radius: 4px; pointer-events: auto;
}
.co-field-label {
  font-size: 12px; font-weight: 600; color: #6b7280;
  margin-bottom: 4px;
}
.co-skip-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease;
  margin-right: 2px;
}
.co-skip-btn:hover {
  background: rgba(244, 67, 54, 0.12);
}
.co-skip-btn:hover :deep(.v-icon) {
  color: #f44336 !important;
}
.co-field-skipped :deep(.v-field) {
  background-color: #f5f5f5;
}
.co-field-skipped :deep(.v-label) {
  text-decoration: line-through;
  opacity: 0.6;
}
.co-restore-link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}
.co-restore-link:hover {
  color: #0d47a1;
}
.doc-list { display: flex; flex-direction: column; gap: 6px; }
.doc-list-item {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px;
  font-size: 12px; color: #334155;
  background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px;
}
.progress-line {
  padding: 6px 10px;
  background: #fef3c7; border-left: 3px solid #f59e0b;
  border-radius: 4px; color: #92400e;
}
.co-footer {
  position: sticky; bottom: 0; z-index: 2;
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px;
  background: #fff; border-top: 1px solid #e5e7eb;
  margin-left: -16px; margin-right: -16px; margin-top: auto;
}
</style>
